import { Box, Stack } from '@chakra-ui/react'
import { useRouter } from 'next/router'
import React from 'react'

import { WORLD_SLUG_QUERY_PARAM_KEY } from '@/client/services/url'

import Select from '@/components/UI/Select'

import useAllWorlds from './useAllWorlds'

const ALL_SUBWORLDS = 'All Subworlds'

function useWorldSlugFromQueryParam(): string | null {
  const router = useRouter()
  const worldSlug = router.query[WORLD_SLUG_QUERY_PARAM_KEY]

  if (worldSlug == null) {
    return null
  }

  return `${worldSlug}`
}

function WorldFinder({
  worldSlug,
  adminIncludeAll,
}: {
  worldSlug: string | null
  adminIncludeAll: boolean
}): JSX.Element {
  const router = useRouter()

  const worlds = useAllWorlds(['slug', 'title'], { adminIncludeAll, numRandomUsersToInclude: 0 })

  const worldOptions = worlds.map(world => ({ label: world.title, value: world.slug }))
  const options = [{ label: ALL_SUBWORLDS, value: ALL_SUBWORLDS }, ...worldOptions]

  const defaultOption = worldSlug != null ? options.find(x => x.value === worldSlug) : options[0]

  const handleWorldChange = React.useCallback(
    async (newWorldSlug: string) => {
      const { [WORLD_SLUG_QUERY_PARAM_KEY]: _, ...rest } = router.query
      const worldSlugParam =
        newWorldSlug === ALL_SUBWORLDS ? {} : { [WORLD_SLUG_QUERY_PARAM_KEY]: newWorldSlug }

      await router.replace({
        query: {
          ...rest,
          ...worldSlugParam,
        },
      })
    },
    [router]
  )

  return (
    <Stack direction="row" spacing="8px" align="center">
      <Box fontWeight="bold">Subworld</Box>
      <Box width="200px">
        <Select
          instanceId="world-finder"
          name="world-finder"
          options={options}
          defaultValue={defaultOption}
          onChange={e => handleWorldChange(e?.value ?? '')}
          isClearable={false}
          components={{
            IndicatorSeparator: () => null,
          }}
        />
      </Box>
    </Stack>
  )
}

export default function useWorldFinder({
  adminIncludeAll = false,
}: {
  adminIncludeAll?: boolean
}): {
  worldSlug: string | null
  WorldFinder: () => JSX.Element
} {
  const worldSlug = useWorldSlugFromQueryParam()

  return {
    worldSlug,
    WorldFinder: () => <WorldFinder worldSlug={worldSlug} adminIncludeAll={adminIncludeAll} />,
  }
}
